import React, { useMemo } from 'react';
import { View, StyleSheet, ViewStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

const EmptySoldIcon = RESIDENTS_SVG_COMPONENTS.EMPTY_SOLD;

interface MarketplaceEmptyStateProps {
  /** Main title text */
  title: string;
  /** Optional subtitle/description text */
  subtitle?: string;
  /** Optional custom icon component - defaults to EMPTY_SOLD */
  icon?: React.ReactNode;
  /** Optional icon size - defaults to 68 */
  iconSize?: number;
  /** Optional container style override */
  style?: ViewStyle;
}

/**
 * Reusable empty state component for marketplace screens
 * Displays an icon with title and optional subtitle
 */
export const MarketplaceEmptyState: React.FC<MarketplaceEmptyStateProps> = ({
  title,
  subtitle,
  icon,
  iconSize = 68,
  style,
}) => {
  const mc = useMarketplaceColors();

  const styles = useMemo(() => StyleSheet.create({
    container: {
      alignItems: 'center',
      gap: 16,
      paddingVertical: 60,
    },
    iconContainer: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    textContainer: {
      alignItems: 'center',
      gap: 6,
      maxWidth: 280,
    },
    title: {
      fontSize: 16,
      fontWeight: '600',
      color: mc.textPrimary,
      lineHeight: 24,
      textAlign: 'center',
    },
    subtitle: {
      fontSize: 14,
      fontWeight: '400',
      color: mc.textMuted,
      lineHeight: 21,
      textAlign: 'center',
    },
  }), [mc]);

  return (
    <View style={[styles.container, style]}>
      <View style={[styles.iconContainer, { width: iconSize, height: iconSize }]}>
        {icon || (
          <EmptySoldIcon 
            width={iconSize} 
            height={iconSize} 
            color={mc.textSecondary}
          />
        )}
      </View>
      
      <View style={styles.textContainer}>
        <Text style={styles.title}>{title}</Text>
        {subtitle && (
          <Text style={styles.subtitle}>{subtitle}</Text>
        )}
      </View>
    </View>
  );
};
